import React, { FC, useCallback, useReducer } from 'react';

import {
  Box,
  useDisclosure,
  Stack,
  UseDisclosureProps,
  Text,
  HStack,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { PageHeaderWithIcon } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { AddButton } from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import {
  ComplianceCheckPassedIcon,
  PolicyIcon,
  RemediateIcon,
  SnoozeIcon,
  TagIcon,
  TrashIcon,
} from 'components/Icons';
import { DeleteConfirmationModal, TableActionsMenu } from 'components/Overlay';
import { exceptionApplyTimeOptions } from 'containers/Findings/Alerts/Components/utils';
import { AddPolicies } from 'containers/PolicyHub/Blueprints/Components/Blueprint/BlueprintPolicies/AddPolicy/AddPolicies';
import { ComplianceBlueprintControls } from 'containers/PolicyHub/Blueprints/Components/Blueprint/BlueprintPolicies/ComplianceBlueprint/ComplianceBlueprintControls';
import { PoliciesTable } from 'containers/PolicyHub/Blueprints/Components/Blueprint/BlueprintPolicies/Policies/PoliciesTable';
import { RemediationModal } from 'containers/PolicyHub/Blueprints/Components/Blueprint/BlueprintPolicies/Policies/Remediation/RemediationModal';
import { useBlueprintPolicyColumns } from 'containers/PolicyHub/Blueprints/hooks/useBlueprintPolicyColumns';
import { actions } from 'containers/PolicyHub/Blueprints/slice';
import { ApplyPolicyException } from 'containers/PolicyHub/Components/ApplyPolicyException/ApplyPolicyException';
import { EditPolicyContext } from 'containers/PolicyHub/Components/EditPolicyContext';
import { PoliciesTableHeader } from 'containers/PolicyHub/Components/PoliciesTableHeader/PoliciesTableHeader';
import { policyTypeOptions } from 'containers/PolicyHub/Components/PoliciesTableHeader/utils';

import { selectBlueprints } from '../../../selectors';

interface Props {
  addPolicy?: UseDisclosureProps;
  action?: 'Create' | 'Show' | 'Add';
}

export const BlueprintPolicies: FC<Props> = props => {
  const { addPolicy } = props;
  const { blueprint, blueprintPolicies, blueprintAction } = useSelector(
    selectBlueprints,
  );
  const excludePolicy = useDisclosure();
  const includePolicy = useDisclosure();
  const removePolicy = useDisclosure();
  const remediation = useDisclosure();
  const editContext = useDisclosure();
  const resourceExceptions = useDisclosure();
  const { policiesTableColumns } = useBlueprintPolicyColumns();
  const dispatch = useDispatch();
  const [exceptionDescription, setExceptionDescription] = React.useState('');
  const [exceptionApplyTime, setExceptionApplyTime] = React.useState(
    exceptionApplyTimeOptions[0],
  );
  const { setLearnMore } = useKnowledgeBase();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      resourceType: {},
      alertCategory: {},
      currentRecord: {},
      searchTerm: '',
      severity: { label: 'Severity', value: undefined },
      policyType: policyTypeOptions[0],
    },
  );

  //get policies
  const getBlueprintPolicies = useCallback(() => {
    dispatch(
      actions.getBlueprintPolicies({
        q: {
          blueprintId: blueprint.data.id,
          alertCategory: state.alertCategory?.value,
          resourceType: state.resourceType?.value,
          searchTerm: state.searchTerm,
          severity: state.severity?.value,
          page: blueprintPolicies.page.info.page_number,
          size: blueprintPolicies.page.info.page_size,
          isCustom: !!state.policyType?.value
            ? state.policyType?.value === 'custom'
            : undefined,
        },
      }),
    );
  }, [
    dispatch,
    blueprint.data,
    state.alertCategory,
    state.resourceType,
    state.searchTerm,
    state.severity,
    state.policyType,
    blueprintPolicies.page.info,
  ]);

  const handleExcludeRule = (e, row) => {
    e.stopPropagation();
    if (blueprint.data.id)
      dispatch(
        actions.excludeRules({
          q: {
            blueprintId: blueprint.data?.id!,
            ruleInstanceMultiCreate: {
              rules: [{ rule_id: row.id }],
            },
          },
          onSuccess: () => {
            excludePolicy.onClose?.();
            getBlueprintPolicies();
          },
        }),
      );
  };

  const handleRemoveRule = row => {
    if (blueprint.data.id)
      dispatch(
        actions.removePolicy({
          q: {
            blueprintId: blueprint.data?.id!,
            ruleInstanceMultiCreate: {
              rules: [{ rule_id: row.id }],
            },
          },
          onSuccess: () => {
            removePolicy.onClose?.();
            getBlueprintPolicies();
          },
        }),
      );
  };

  const renderActions = ({ row }) => {
    const menuItems = [
      ...(addPolicy?.isOpen
        ? [
            {
              label: 'Include policy',
              icon: <ComplianceCheckPassedIcon />,
              onClick: e => {
                e.stopPropagation();
                includePolicy.onOpen();
                updateState({ currentRecord: row });
              },
            },
          ]
        : [
            {
              label: 'Edit context',
              icon: <TagIcon w={5} h={5} />,
              onClick: e => {
                e.stopPropagation();
                editContext.onOpen();
                updateState({ currentRecord: row });
              },
              isDisabled: blueprint.data?.created_by === 'system',
            },
            {
              label: 'Apply exception',
              icon: <SnoozeIcon />,
              onClick: e => {
                e.stopPropagation();
                excludePolicy.onOpen();
                updateState({ currentRecord: row });
              },
              isDisabled: blueprint.data?.created_by === 'system',
            },
            {
              label: 'Available remediations',
              icon: <RemediateIcon />,
              onClick: e => {
                e.stopPropagation();
                remediation.onOpen();
                updateState({ currentRecord: row });
              },
            },
            {
              label: 'Remove policy',
              icon: <TrashIcon color="red" />,
              onClick: e => {
                e.stopPropagation();
                removePolicy.onOpen();
                updateState({ currentRecord: row });
              },
              isDisabled: blueprint.data?.created_by === 'system',
            },
          ]),
    ];

    return (
      <Box onClick={e => e.stopPropagation()}>
        <TableActionsMenu menuItems={menuItems} />
      </Box>
    );
  };

  const columns = [
    ...policiesTableColumns,
    {
      header: 'Actions',
      render: renderActions,
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];

  const updateValue = useCallback((field, value) => {
    updateState({ [field]: value });
  }, []);

  return (
    <Stack h="full" spacing={4}>
      <HStack w="full" justifyContent="space-between">
        <Box>
          <PageHeaderWithIcon
            label={(addPolicy?.isOpen ? 'Add ' : '') + 'Policies'}
            reversed
            icon={
              addPolicy?.isOpen ? <ComplianceCheckPassedIcon /> : <PolicyIcon />
            }
          />
        </Box>
        <HStack>
          <LearnMoreButton
            onOpen={() => setLearnMore('policies')}
            label="Policies"
          />
          {!!addPolicy && !addPolicy?.isOpen && (
            <AddButton
              onClick={() => addPolicy?.onOpen?.()}
              label="Add Policy"
            />
          )}
        </HStack>
      </HStack>
      <BorderedBox h="full" w="full" flex={1} p={4}>
        <Stack spacing={5} h="full">
          <PoliciesTableHeader
            resourceType={state.resourceType}
            alertCategory={state.alertCategory}
            severity={state.severity}
            policyType={state.policyType}
            updateValue={updateValue}
            compliance={blueprint?.data?.compliance}
          />
          {!blueprint.data?.compliance && !addPolicy?.isOpen && (
            <Box h="full" flex={1}>
              <PoliciesTable
                columns={columns}
                resourceExceptions={resourceExceptions}
                getBlueprintPolicies={getBlueprintPolicies}
              />
            </Box>
          )}
          {!!blueprint.data.compliance && !addPolicy?.isOpen && (
            <Box h="full" flex={1}>
              <ComplianceBlueprintControls
                searchTerm={state.searchTerm}
                addPolicy={addPolicy!}
              />
            </Box>
          )}
          {addPolicy?.isOpen && (
            <AddPolicies
              searchTerm={state.searchTerm}
              alertCategory={state.alertCategory?.value}
              resourceType={state.resourceType?.value}
              columns={columns}
              policy={state.currentRecord}
              includePolicy={includePolicy}
            />
          )}
          {remediation.isOpen && (
            <RemediationModal
              state={remediation}
              policy={state.currentRecord}
            />
          )}
          {editContext.isOpen && (
            <EditPolicyContext
              isOpen={editContext.isOpen}
              onClose={editContext.onClose}
              row={state.currentRecord}
              blueprintId={blueprint.data.id}
              onSuccess={getBlueprintPolicies}
            />
          )}
          {excludePolicy.isOpen && (
            <ApplyPolicyException
              isOpen={excludePolicy.isOpen}
              onClose={excludePolicy.onClose}
              rule={state.currentRecord}
              blueprint={blueprint.data}
              onConfirm={e => handleExcludeRule(e, state.currentRecord)}
              exceptionDescription={exceptionDescription}
              setExceptionDescription={setExceptionDescription}
              exceptionApplyTime={exceptionApplyTime}
              setExceptionApplyTime={setExceptionApplyTime}
            />
          )}
          {removePolicy.isOpen && (
            <DeleteConfirmationModal
              confirmationText={
                <Text>
                  Please type <Text as="b">CONFIRM</Text> to remove policy.
                </Text>
              }
              name="CONFIRM"
              header="Exclude rule confirmation"
              label="Exclude"
              onConfirm={() => handleRemoveRule(state.currentRecord)}
              isOpen={removePolicy.isOpen}
              onClose={removePolicy.onClose!}
              isLoading={blueprintAction.isLoading}
            />
          )}
        </Stack>
      </BorderedBox>
    </Stack>
  );
};
